import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ModalsKeys, Nullable } from 'global';
import { useModal } from 'hooks/useModal';
import { useMutation, useQuery } from '@apollo/client';
import { AddCircle, Loop } from '@mui/icons-material';
import { grey } from '@mui/material/colors';
import { Button, Skeleton, Stack } from '@mui/material';
import { Equipment } from 'modules/desking/types';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { ListingForm } from 'components/shared/TableListing/types';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import {
  useQuote,
  InvoiceDetailsListing,
  NetFinanceAmountListing,
  collectQuoteFromInvoiceForm,
  collectInvoiceDTOFromQuote,
  mapQuoteEquipmentToSync,
  CalculatedTotalsByLevel,
  Quote,
  QuoteChargeListingEntity,
  QuoteChargeType,
} from 'modules/quote';
import { useInvoice } from 'modules/invoice';
import { useEquipmentActions } from 'modules/equipment';
import {
  dealBuilderApi,
  UpdateDealBuilderQuoteResponse,
  UpdateDealBuilderQuoteVariables,
  UpsertDealBuilderEquipmentsResponse,
  UpsertDealBuilderEquipmentsVariables,
} from '../../../api';
import { useDealBuilderContext } from '../../../context/DealBuilderContext';
import { StepFooterNav } from '../../components/StepFooterNav';
import { CustomerDepositForm } from './CustomerDepositForm';
import { TradeInSection } from './TradeInSection';

type EquipmentUpdateDTO = Parameters<ReturnType<typeof useEquipmentActions>['onUpdateEquipment']>[0];
type InvoiceUpdateInput = Parameters<ReturnType<typeof useInvoice>['handleUpdateInvoice']>[0];

const usePricingMutations = () => {
  const { activeEntity, setActiveEntity } = useDealBuilderContext();
  const { onUpdateEquipment } = useEquipmentActions();
  const { handleUpdateInvoice } = useInvoice();

  const [updateDealBuilderQuoteMutation] = useMutation<UpdateDealBuilderQuoteResponse, UpdateDealBuilderQuoteVariables>(
    dealBuilderApi.updateDealBuilderQuote
  );
  const [upsertEquipmentsMutation] = useMutation<
    UpsertDealBuilderEquipmentsResponse,
    UpsertDealBuilderEquipmentsVariables
  >(dealBuilderApi.upsertDealBuilderEquipments);

  const quoteTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const equipmentUpdateTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const invoiceTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const updateQuote = useCallback(
    async (quoteId: string, input: Record<string, unknown>) => {
      if (!quoteId) return;
      clearTimeout(quoteTimerRef.current);
      quoteTimerRef.current = setTimeout(async () => {
        const result = await updateDealBuilderQuoteMutation({ variables: { quoteId, input } });
        const updatedQuote = result.data?.dealBuilder?.updateDealBuilderQuote?.entity;
        if (updatedQuote && activeEntity) {
          setActiveEntity({ ...activeEntity, quote: updatedQuote });
        }
      }, 1500);
    },
    [updateDealBuilderQuoteMutation, setActiveEntity]
  );

  const updateEquipments = useCallback(
    (dtos: EquipmentUpdateDTO[]) => {
      clearTimeout(equipmentUpdateTimerRef.current);
      equipmentUpdateTimerRef.current = setTimeout(() => {
        dtos.forEach(dto => onUpdateEquipment(dto, { withNotification: false }));
      }, 1500);
    },
    [onUpdateEquipment]
  );

  const updateInvoice = useCallback(
    (input: InvoiceUpdateInput) => {
      clearTimeout(invoiceTimerRef.current);
      invoiceTimerRef.current = setTimeout(() => {
        handleUpdateInvoice(input);
      }, 1500);
    },
    [handleUpdateInvoice]
  );

  return { updateQuote, updateEquipments, updateInvoice };
};

export const PricingStep = () => {
  const {
    sourceData: { vo },
    activeEntity,
    setActiveEntity,
    equipment: { selectedEquipmentItem },
    pricing: { calculatedTotals, setCalculatedTotals },
  } = useDealBuilderContext();
  const { handleApplyFeesAndTaxes, isApplyingFeesAndTaxes } = useInvoice();
  const { handleFetchQuoteById } = useQuote();
  const { updateQuote, updateEquipments, updateInvoice } = usePricingMutations();
  const { handleOpen, handleClose } = useModal(ModalsKeys.Confirmation);
  const chargesSubmitRef = useRef<{ submit: () => ListingForm<QuoteChargeListingEntity> } | null>(null);

  const vendorOpportunityId = vo?.vendorOpportunityId ?? '';
  const dynamicsVOId = vo?.dynamicsVendorOpportunityId ?? '';

  const [isRefreshingQuote, setIsRefreshingQuote] = useState(false);

  // Do not reinitialize the listing when quote data changes
  const stableQuote = useMemo(
    () => (activeEntity?.quote ?? null) as unknown as Nullable<Quote>,
    [activeEntity?.quote?.quoteId, isRefreshingQuote]
  );

  const { data: equipmentQueryData, loading: equipmentLoading } = useQuery<{ equipment: Equipment }, { id: string }>(
    dealBuilderApi.getEquipmentDetailsById,
    {
      variables: { id: selectedEquipmentItem?.equipment.id ?? '' },
      skip: !selectedEquipmentItem,
    }
  );

  const handleSubscribeTotals = useCallback(
    async (calc: CalculatedTotalsByLevel, fields?: QuoteChargeListingEntity[]) => {
      setCalculatedTotals(calc);

      const formValues = fields ?? chargesSubmitRef.current?.submit()?.bodyFields ?? [];

      const quoteDTO = collectQuoteFromInvoiceForm(formValues);

      // Trade-ins are persisted separately by TradeInSection; exclude the empty placeholder so it isn't overwritten
      const { equipmentTradeIns: _equipmentTradeIns, ...quoteInput } = quoteDTO;

      await updateQuote(stableQuote?.quoteId!, { ...quoteInput, vendorOpportunityId });

      const equipmentDTOs = formValues
        .filter(({ type }) => type === QuoteChargeType.Equipment)
        .map(({ equipment }) => ({
          ...mapQuoteEquipmentToSync(equipment!),
          vendorOpportunityId,
          equipmentId: equipment!.equipmentId,
          dynamicsVendorOpportunityId: dynamicsVOId,
        }));
      updateEquipments(equipmentDTOs);

      const invoiceDTO = collectInvoiceDTOFromQuote(quoteDTO);
      updateInvoice({ VOId: vendorOpportunityId, entityId: dynamicsVOId, fieldsObj: invoiceDTO });
    },
    [vendorOpportunityId, dynamicsVOId, updateQuote, updateEquipments, updateInvoice, setCalculatedTotals]
  );

  const equipmentList = useMemo<Equipment[]>(() => {
    if (!equipmentQueryData?.equipment) return [];
    return [{ ...equipmentQueryData.equipment, vendorOpportunityId }];
  }, [equipmentQueryData?.equipment, vendorOpportunityId]);

  const handleReapplyTaxesAndFees = useCallback(() => {
    handleOpen({
      title: 'Confirmation',
      message:
        'Warning - all existing taxes, fees, charges, and discounts on this invoice and associated equipment will be removed and replaced. Continue?',
      onConfirm: async () => {
        handleClose();
        setIsRefreshingQuote(true);
        await handleApplyFeesAndTaxes(vendorOpportunityId);

        const quoteId = activeEntity?.quote?.quoteId;
        const refreshedQuote = quoteId ? await handleFetchQuoteById(quoteId) : null;
        if (refreshedQuote && activeEntity) {
          setActiveEntity({
            ...activeEntity,
            quote: refreshedQuote as unknown as typeof activeEntity.quote,
          });
        }
        setIsRefreshingQuote(false);
      },
    });
  }, [
    handleOpen,
    handleClose,
    handleApplyFeesAndTaxes,
    handleFetchQuoteById,
    vendorOpportunityId,
    activeEntity,
    setActiveEntity,
  ]);

  return (
    <CardContainer
      title="Pricing"
      subTitle="Edit pricing line items, add charges, and review pricing before building a payment structure"
    >
      <RenderWithFallback
        isLoading={equipmentLoading || isRefreshingQuote}
        fallBack={
          <Stack gap="1rem">
            <Skeleton variant="rectangular" width="100%" height={200} />
            <Skeleton variant="rectangular" width="100%" height={100} />
            <Skeleton variant="rectangular" width="100%" height={100} />
          </Stack>
        }
      >
        <InvoiceDetailsListing
          VOId={vendorOpportunityId}
          quote={stableQuote}
          equipmentList={equipmentList}
          dynamicsVOId={dynamicsVOId}
          chargesSubmitRef={chargesSubmitRef}
          onSubscribeTotals={handleSubscribeTotals}
          actionPanelBottom={({ onAddCharge }) => (
            <Stack direction="row" gap="1rem" sx={{ paddingBottom: '0.5rem', borderBottom: `1px solid ${grey[300]}` }}>
              <Button onClick={onAddCharge} startIcon={<AddCircle />} size="small">
                Add Invoice Item
              </Button>
              <Button
                sx={{ ml: 'auto' }}
                startIcon={<Loop />}
                loading={isApplyingFeesAndTaxes}
                onClick={handleReapplyTaxesAndFees}
              >
                Reapply Taxes and Fees
              </Button>
            </Stack>
          )}
        />

        <CustomerDepositForm />

        <TradeInSection />

        <NetFinanceAmountListing {...calculatedTotals} />
      </RenderWithFallback>

      <StepFooterNav />
    </CardContainer>
  );
};
